import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { GlassCountries, STORAGE_KEY, GlassManufacturers } from 'src/app/utils/Constants';
import { GlassInfoModalComponent } from '../glass-info-modal/glass-info-modal.component';
import { GlassBybrandResultComponent } from '../glass-bybrand-result/glass-bybrand-result.component';
import { StorageService } from 'src/app/services/storage.service';
import { WindowtypeUpdatesService } from 'src/app/services/windowtype-updates-service';
type GlassMode = 'by-glass' | 'by-brand' | 'manual';
import {
  GlassFilterModalComponent,
  GlassFilterState,
} from '../glass-filter-modal/glass-filter-modal.component';
import { CalculationAutoUpdateService } from 'src/app/services/calculation-auto-update.service';
import { TranslateService } from '@ngx-translate/core';
interface Glass {
  id: string;
  glazingCount: number;
  ug: number;
  interiorGlassThickness: number;
  exteriorGlassThickness: number;
  middleGlassThickness?: number | null;
  cavities: number[];
  name: string;
  type: string;
  thickness: number;
  category: string;
  uValue: number;
  gValue: number;
  lightTransmittance: number;
  coating: string;
  manufacturer: number;
  isLowE: boolean;
  region: number[];
  structure: string;
  deleted?: boolean;
}
@Component({
  selector: 'app-edit-glass',
  templateUrl: './edit-glass.component.html',
  styleUrls: ['./edit-glass.component.scss'],
  standalone: false,
})

export class EditGlassComponent implements OnInit {
  @Input() isComparisonMode: boolean = false;
  mode: GlassMode = 'by-brand';
  SAINT_GOBAIN_ID = 1;
  glasses: Glass[] = [];
  manufacturers = GlassManufacturers;
  countries = GlassCountries;

  selectedManufacturerId: number | null = this.manufacturers[0]?.id ?? null;
  selectedCountryId: number = this.countries[0]?.id ?? 0;
  selectedGlass: Glass | null = null;

  eManGlassName: string = 'man';
  eManGlassUg: number = 1.0;
  eManGlassGValue: number = 50;
  eManGlassGlazing: number = 2;
  eManGlassInternal: number = 4;
  eManGlassExternal: number = 4;
  eManGlassMiddle: number = 3;
  eManCavity1: number = 16;
  eManCavity2: number = 16;
  eManCavity3: number = 16;

  gValueOptions: number[] = Array.from({ length: 99 }, (_, i) => i + 1); // 1-99
  glazingOptions: number[] = [1, 2, 3, 4]; // 1-4
  glassThicknessOptions: number[] = Array.from({ length: 23 }, (_, i) => i + 2); // 2-24
  cavityOptions: number[] = Array.from({ length: 29 }, (_, i) => i + 8); // 8-36

  byGlassValue = '';
  manualValue = '';

  glassCorrectionFactor: boolean = false;

  constructor(private modalController: ModalController, private storageService: StorageService,
    private windowtypeUpdatesService: WindowtypeUpdatesService, private calcAutoUpdate: CalculationAutoUpdateService, private translate: TranslateService) { }

  glassFilter: GlassFilterState = {
    ug: null,
    structure: null,
    lightTransmittance: null,
    gValue: null,
    category: null,
    regionId: 0,
  };

  get isFilterActive(): boolean {
    return this.glassFilter.ug !== null ||
      !!this.glassFilter.structure ||
      this.glassFilter.lightTransmittance !== null ||
      this.glassFilter.gValue !== null ||
      !!this.glassFilter.category ||
      this.glassFilter.regionId !== 0;
  }

  ngOnInit() { }
  async ionViewWillEnter() {
    await this.loadGlassesFromStorage();
    await this.init();
  }

  private async loadGlassesFromStorage(): Promise<void> {
    try {
      const cachedData = await this.storageService.get(STORAGE_KEY);

      if (cachedData) {
        const masterData = JSON.parse(cachedData);
        this.glasses = masterData.glasses || [];
        console.log('Loaded glasses from localStorage:', this.glasses.length);
      } else {
        console.warn('No master data found in localStorage');
        this.glasses = [];
      }
    } catch (error) {
      console.error('Error loading glasses from storage:', error);
      this.glasses = [];
    }
  }

  private async init(): Promise<void> {
    try {
      const res = this.isComparisonMode
        ? await this.storageService.getComparedProject()
        : await this.storageService.getProject();

      let storedProject = res ? JSON.parse(res) : null;

      if (storedProject?.windowglas) {
        const glass = storedProject.windowglas;

        // Get the mode from storage
        this.mode = storedProject?.windowGlassMode || 'by-glass';
        this.glassCorrectionFactor = storedProject?.glassCorrectionFactor || false;

        // Restore based on mode
        if (this.mode === 'by-glass') {
          // BY-GLASS: Just set the selected glass
          this.selectedGlass = glass;
          this.selectedCountryId = storedProject?.windowGlassByGlassSettings?.countryId || this.countries[0]?.id;

        } else if (this.mode === 'by-brand') {
          // BY-BRAND: Restore manufacturer, region, and selected glass
          const settings = storedProject?.windowGlassBrandSettings;
          if (settings) {
            this.selectedManufacturerId = settings.manufacturerId || this.manufacturers[0]?.id;
            this.selectedCountryId = settings.countryId || this.countries[0]?.id;
          }
          this.selectedGlass = glass;

        } else if (this.mode === 'manual') {
          // MANUAL: Restore all manual input values
          this.eManGlassName = glass.name || 'man';
          this.eManGlassUg = glass.ug || 1.0;
          this.eManGlassGValue = glass.gValue || 50;
          this.eManGlassGlazing = glass.glazingCount || 2;
          this.eManGlassExternal = glass.exteriorGlassThickness || 4;
          this.eManGlassInternal = glass.interiorGlassThickness || 4;
          this.eManGlassMiddle = glass.middleGlassThickness || 3;
          // Restore cavities from array
          if (glass.cavities && Array.isArray(glass.cavities)) {
            this.eManCavity1 = glass.cavities[0] || 16;
            this.eManCavity2 = glass.cavities[1] || 16;
            this.eManCavity3 = glass.cavities[2] || 16;
          }
        }
      }

      console.log('Initialized glass data - Mode:', this.mode, 'Glass:', this.selectedGlass);
    } catch (error) {
      console.error('Error initializing glass data:', error);
    }
  }

  onModeChange(event: CustomEvent) {
    const value = event.detail.value as string | undefined;
    if (value === 'by-glass' || value === 'by-brand' || value === 'manual') {
      this.mode = value;
      // Reset filter when switching modes
      this.glassFilter = {
        ug: null, structure: null, lightTransmittance: null,
        gValue: null, category: null, regionId: 0,
      };
    }
  }

  //by-glass
  // getFilteredGlasses(): Glass[] {
  //   return this.glasses.filter(glass =>
  //     // !glass.deleted &&
  //     glass.manufacturer === this.SAINT_GOBAIN_ID &&
  //     this.glassBelongsToRegion(glass, this.selectedCountryId)
  //   );
  // }
  getFilteredGlasses(): Glass[] {
    return this.glasses.filter(glass => {
      if (glass.deleted) return false;
      if (glass.manufacturer !== this.SAINT_GOBAIN_ID) return false;
      if (!this.glassBelongsToRegion(glass, this.glassFilter.regionId)) return false;
      if (this.glassFilter.category && glass.category !== this.glassFilter.category) return false;
      if (this.glassFilter.structure && glass.structure !== this.glassFilter.structure) return false;
      const ug = parseFloat(glass.ug.toFixed(1));
      if (this.glassFilter.ug !== null && ug > this.glassFilter.ug) return false;
      if (this.glassFilter.lightTransmittance !== null && glass.lightTransmittance < this.glassFilter.lightTransmittance) return false;
      if (this.glassFilter.gValue !== null && glass.gValue < this.glassFilter.gValue) return false;
      return true;
    });
  }

  async openFilterModal() {
    // Pass only the relevant glass pool depending on mode
    const glassPool = this.mode === 'by-brand'
      ? this.glasses.filter(g => g.manufacturer === this.selectedManufacturerId)
      : this.getFilteredGlasses();

    const modal = await this.modalController.create({
      component: GlassFilterModalComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        glasses: glassPool,
        countries: this.countries,
        currentFilter: { ...this.glassFilter },
      },
    });

    await modal.present();
    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data?.filter) {
      this.glassFilter = data.filter;

      // Sync regionId to the main region selector so they stay consistent
      this.selectedCountryId = this.glassFilter.regionId || (this.countries[0]?.id ?? 0);
    }
  }

  // Check if glass belongs to selected region
  glassBelongsToRegion(glass: any, selectedRegion: number): boolean {
    if (!glass.region || !Array.isArray(glass.region)) {
      return false;
    }
    const neg = -selectedRegion;
    let isEU = false;
    let result = false;

    for (let r of glass.region) {
      if (r === 1) isEU = true;
      if (r === selectedRegion) result = true;
      if (r === 0) result = true; // Worldwide
      if (selectedRegion !== 0 && r === neg) result = false;
    }

    // Check if country is in EU
    if (isEU && selectedRegion !== 0) {
      const country = this.countries.find(c => c.id === selectedRegion);
      if (country?.eu) result = true;
    }
    return result;
  }

  // Select a glass
  selectGlass(glass: Glass) {
    this.selectedGlass = glass;
    console.log('Selected glass:', glass);
  }

  async showGlassInfo(glass: Glass) {
    console.log('Show info for:', glass);

    const modal = await this.modalController.create({
      component: GlassInfoModalComponent,
      cssClass: 'glass-info-modal',
      componentProps: { glass }
    });

    await modal.present();
  }

  // Get filtered glasses for by-brand mode (selected manufacturer + region)
  // getFilteredGlassesByBrand(): Glass[] {
  //   return this.glasses.filter(glass =>
  //     // !glass.deleted &&
  //     glass.manufacturer === this.selectedManufacturerId &&
  //     this.glassBelongsToRegion(glass, this.selectedCountryId)
  //   );
  // }
  getFilteredGlassesByBrand(): Glass[] {
    return this.glasses.filter(glass => {
      if (glass.deleted) return false;
      if (glass.manufacturer !== this.selectedManufacturerId) return false;
      if (!this.glassBelongsToRegion(glass, this.selectedCountryId)) return false;
      return true;
    });
  }

  getImageForGlazing(glazingCount: number): string {
    const imageMap: Record<number, string> = {
      1: 'assets/images/typen/Single.svg',
      2: 'assets/images/typen/Double.svg',
      3: 'assets/images/typen/Triple.svg',
      4: 'assets/images/typen/Quadruple.svg'
    };
    return imageMap[glazingCount] || 'assets/images/typen/Double.svg';
  }

  private async saveToStorage(payload: any): Promise<void> {
    if (this.isComparisonMode) {
      await this.storageService.updateComparedProject(payload);
    } else {
      await this.storageService.updateProject(payload);
      await this.calcAutoUpdate.triggerAutoUpdate('glass', this.isComparisonMode);
    }
  }

  async next() {
    if (this.mode === 'by-glass') {
      if (this.selectedGlass) {
        await this.saveToStorage({
          windowglas: this.selectedGlass,
          windowGlassMode: this.mode,
          windowGlassByGlassSettings: { countryId: this.selectedCountryId },
          glassCorrectionFactor: this.glassCorrectionFactor
        });
        await this.autoUpdateFrameForGlazing(this.selectedGlass?.glazingCount);
        console.log('Saved by-glass selection:', this.selectedGlass);
        await this.modalController.dismiss({
          selectedGlass: this.selectedGlass
        }, 'confirm');
      }
    } else if (this.mode === 'by-brand') {
      // BY-BRAND MODE
      const filteredGlasses = this.getFilteredGlassesByBrand();
      if (filteredGlasses.length === 0) {
        console.log('No glasses found for selected criteria');
        return;
      }

      const modal = await this.modalController.create({
        component: GlassBybrandResultComponent,
        cssClass: 'configuration-modal',
        componentProps: {
          glasses: filteredGlasses,
          manufacturerId: this.selectedManufacturerId,
          countryId: this.selectedCountryId,
        }
      });
      await modal.present();
      const { data, role } = await modal.onWillDismiss();
      if (role === 'confirm' && data?.selectedGlass) {
        await this.saveToStorage({
          windowglas: data.selectedGlass,
          windowGlassMode: this.mode,
          windowGlassBrandSettings: {
            manufacturerId: this.selectedManufacturerId,
            countryId: this.selectedCountryId
          },
          glassCorrectionFactor: this.glassCorrectionFactor
        });
        await this.autoUpdateFrameForGlazing(data.selectedGlass.glazingCount);
        await modal.dismiss();
        await this.modalController.dismiss(data, 'confirm');
      }
    }
    else if (this.mode === 'manual') {
      if (this.validateForm()) {
        const manualGlass = this.createManualGlass();
        console.log("manualGlass", manualGlass);
        await this.saveToStorage({
          windowglas: manualGlass,
          windowGlassMode: this.mode,
          glassCorrectionFactor: this.glassCorrectionFactor
        });
        await this.autoUpdateFrameForGlazing(manualGlass.glazingCount);
        await this.modalController.dismiss({
          selectedGlass: manualGlass
        }, 'confirm');
      }
    }
  }

  private async autoUpdateFrameForGlazing(newGlazingCount: number): Promise<void> {
    const res = this.isComparisonMode
      ? await this.storageService.getComparedProject()
      : await this.storageService.getProject();
    const project = res ? JSON.parse(res) : null;
    if (!project?.windowframe?.system) return; // only for by-brand

    const windowTypeId = project.windowtype || 2;
    await this.windowtypeUpdatesService.autoUpdateByBrandFrame(
      windowTypeId,
      newGlazingCount,
      this.isComparisonMode
    );
  }

  cancel() {
    console.log("dismiss called ")
    this.modalController.dismiss(null, 'cancel');
  }

  onGlazingChange(): void {
    // Reset cavities based on glazing type
    if (this.eManGlassGlazing === 1) {
      this.eManCavity1 = 16;
      this.eManCavity2 = 16;
      this.eManCavity3 = 16;
    } else if (this.eManGlassGlazing === 2) {
      this.eManCavity1 = 16;
      this.eManCavity2 = 16;
      this.eManCavity3 = 16;
    } else if (this.eManGlassGlazing === 3) {
      this.eManCavity1 = 12;
      this.eManCavity2 = 12;
      this.eManCavity3 = 16;
    } else if (this.eManGlassGlazing === 4) {
      this.eManCavity1 = 12;
      this.eManCavity2 = 12;
      this.eManCavity3 = 12;
    }
  }

  // Check if cavity should be visible based on glazing type
  showCavity1(): boolean {
    return this.eManGlassGlazing === 2; // Only for double glazing
  }

  showCavity2(): boolean {
    return this.eManGlassGlazing === 3; // Only for triple glazing
  }

  showCavity1And2(): boolean {
    return this.eManGlassGlazing === 3; // For triple glazing show both
  }

  showCavity1And2And3(): boolean {
    return this.eManGlassGlazing === 4; // For quadruple glazing show all three
  }

  validateForm(): boolean {
    if (this.mode === 'manual') {
      return !!(
        this.eManGlassName &&
        this.eManGlassName.trim() !== '' &&
        this.eManGlassUg !== null &&
        this.eManGlassUg > 0
      );
    }
    return false;
  }

  createManualGlass(): Glass {
    // Build structure string
    let structure = '';

    if (this.eManGlassGlazing === 1) {
      // Single glazing: just the glass thickness
      structure = `${this.eManGlassExternal}`;
    } else if (this.eManGlassGlazing === 2) {
      // Double glazing: ext/cavity1/int
      structure = `${this.eManGlassExternal}/${this.eManCavity1}/${this.eManGlassInternal}`;
    } else if (this.eManGlassGlazing === 3) {
      // Triple glazing: ext/cavity1/middle/cavity2/int
      structure = `${this.eManGlassExternal}/${this.eManCavity1}/${this.eManGlassMiddle}/${this.eManCavity2}/${this.eManGlassInternal}`;
    } else if (this.eManGlassGlazing === 4) {
      // Quadruple glazing: ext/cavity1/ /cavity2/ /cavity3/int
      structure = `${this.eManGlassExternal}/${this.eManCavity1}/ /${this.eManCavity2}/ /${this.eManCavity3}/${this.eManGlassInternal}`;
    }

    // Build cavities array
    const cavities: number[] = [];
    if (this.eManGlassGlazing >= 2) cavities.push(this.eManCavity1);
    if (this.eManGlassGlazing >= 3) cavities.push(this.eManCavity2);
    if (this.eManGlassGlazing === 4) cavities.push(this.eManCavity3);

    return {
      id: '0',
      glazingCount: this.eManGlassGlazing,
      ug: this.eManGlassUg,
      interiorGlassThickness: this.eManGlassInternal,
      exteriorGlassThickness: this.eManGlassExternal,
      middleGlassThickness: this.eManGlassGlazing === 3 ? this.eManGlassMiddle : null,
      cavities: cavities,
      name: this.eManGlassName,
      type: '',
      thickness: 0,
      category: '',
      uValue: 0,
      gValue: this.eManGlassGValue,
      lightTransmittance: 0,
      coating: '',
      manufacturer: 0,
      isLowE: false,
      region: [],
      structure: structure
    };
  }

  isNextButtonDisabled(): boolean {
    if (this.mode === 'by-glass') {
      // Disable if no glass is selected
      return !this.selectedGlass;
    } else if (this.mode === 'by-brand') {
      // Never disable for by-brand (will check glasses in next() method)
      return false;
    } else if (this.mode === 'manual') {
      // Disable if form is not valid
      return !this.validateForm();
    }
    return true;
  }

  get isEuOnlyManufacturer(): boolean {
    return this.selectedManufacturerId === 3 || this.selectedManufacturerId === 4;
  }

  get availableCountries(): any[] {
    if (!this.isEuOnlyManufacturer) {
      return this.countries;
    }

    return this.countries.filter(c => c.id === 1 || c.eu === true);
  }

  get sortedAvailableCountries(): any[] {
    const top = this.availableCountries.slice(0, 2);
    const rest = this.availableCountries
      .slice(2)
      .sort((a, b) =>
        this.translate.instant(a.name).localeCompare(
          this.translate.instant(b.name),
          this.translate.currentLang || undefined,
          { sensitivity: 'base' }
        )
      );

    return [...top, ...rest];
  }

  onManufacturerChange(): void {
    // Reset filters when manufacturer changes
    this.glassFilter = {
      ug: null,
      structure: null,
      lightTransmittance: null,
      gValue: null,
      category: null,
      regionId: 0,
    };

    // For manufacturer 3 and 4, only EU-compatible regions should remain selectable
    if (this.isEuOnlyManufacturer) {
      const currentAllowed = this.availableCountries.some(c => c.id === this.selectedCountryId);
      if (!currentAllowed) {
        // default to EU or first EU-allowed option
        this.selectedCountryId = this.availableCountries.find(c => c.id === 1)?.id
          ?? this.availableCountries[0]?.id
          ?? 0;
      }
    }
  }
}

